/** @jsxImportSource @emotion/react */
import { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { GoBackLinkButton } from "../../../components/GoBackLinkButton";
import { routes } from "../../../appRoutingHelpers";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import {
  useCreateNews,
  useGetCountries,
  useGetNews,
  useUpdateNews,
  useUploadNewsImage,
} from "../../../service/api";
import {
  CreateNews,
  NewsForm,
  NewsListItem,
  UpdateNews,
} from "../model/NewsListSuccessResponse";
import { UploadImage } from "../../../service/api-response/UploadImage";
import { toastr } from "react-redux-toastr";
import { StickyBottomBar } from "../../../components/StickyBottomBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { SpinnerBlock } from "../../common/Spinner";
import { NewsGeneralInformationSection } from "./NewsGeneralInformationSection";
import { NewsImageSection } from "./NewsImageSection";
import { PageWrapper } from "../../../components/page-skeleton/PageWrapper";
import { StyledForm } from "../../../components/StyledForm";
import { stickyBottomBarBodyStyle } from "../app-home/generic-styles";
import { getCountrySpecificValues } from "../../../util";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";
import { ButtonAction } from "../../../components/NewButtons";

const newsSchema = yup.object().shape({
  title: yup.string().required("Please enter title to continue"),
  description: yup.string().required("Please enter description to continue"),
  externalUrl: yup.string().url("Please enter a valid URL").optional(),
  files: yup.array().when("$isEdit", (isEdit, schema) => {
    if (!isEdit) {
      return schema.min(1, "Please add an image to continue");
    }
    return;
  }),
  imageUrl: yup.string().when("$isEdit", (isEdit, schema) => {
    if (isEdit) {
      return schema.required("Please add an image to continue");
    }
    return;
  }),
  countryCode: yup.string().required("Please choose country code to continue"),
});

export const AppNewsUpsert = () => {
  const { countryCode } = getCountrySpecificValues();
  const { id } = useParams<{
    id: string;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const isEdit = !!id;
  const methods = useForm<NewsForm>({
    resolver: yupResolver(newsSchema),
    defaultValues: {
      files: [],
      countryCode: countryCode,
      externalUrl: "",
    },
    context: {
      isEdit: isEdit,
    },
  });
  const navigate = useNavigate();
  const { reset, handleSubmit } = methods;

  const {
    mutateAsync: uploadImage,
    isLoading: imageUploadIsLoading,
  } = useUploadNewsImage();

  const {
    data: countries,
    isLoading: countriesAreLoading,
    error: listCountriesError,
  } = useGetCountries();

  const {
    data: news,
    isLoading: newsIsLoading,
    error: getNewsError,
  } = useGetNews(organizationKeyName, id);

  const {
    mutateAsync: createNews,
    isLoading: creationIsLoading,
  } = useCreateNews();

  const {
    mutateAsync: updateNews,
    isLoading: updateIsLoading,
  } = useUpdateNews();

  const isLoading =
    creationIsLoading || updateIsLoading || imageUploadIsLoading;

  const createNewsWithUpload = async (data: NewsListItem, files: File[]) => {
    const request: CreateNews = {
      organizationKeyName,
      data,
    };
    const newsId = await createNews(request);
    const file = files[0];
    const uploadImageRequest: UploadImage = {
      file,
      id: newsId,
      organizationKeyName,
    };
    await uploadImage(uploadImageRequest);
  };

  const onSubmit = async (news: NewsForm) => {
    const { files, externalUrl, ...rest } = news;
    const data: NewsListItem = {
      ...rest,
      externalUrl: externalUrl || null,
    };
    try {
      if (isEdit) {
        const request: UpdateNews = {
          organizationKeyName,
          id: id!,
          data,
        };
        await updateNews(request);
      } else {
        await createNewsWithUpload(data, files);
      }
      toastr.success(
        "Success",
        `You succesfully ${isEdit ? "updated" : "created"} the news`
      );
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    } finally {
      navigate(routes.news(organizationKeyName));
    }
  };

  useEffect(() => {
    if (isEdit && news) {
      const { externalUrl, ...rest } = news;
      reset({
        ...rest,
        externalUrl: externalUrl || "",
      });
    }
  }, [news, reset, isEdit]);

  const buttonText = isEdit ? "Save changes" : "Create news";

  useHandleFetchError(getNewsError);
  useHandleFetchError(listCountriesError);

  if (newsIsLoading || countriesAreLoading) {
    return <SpinnerBlock />;
  }

  return (
    /* @ts-ignore */
    <FormProvider {...methods}>
      <StyledForm>
        <PageWrapper>
          <GoBackLinkButton
            to={routes.news(organizationKeyName)}
            text="Back to all news"
          />
          <NewsGeneralInformationSection countries={countries || []} />
          <NewsImageSection isEdit={isEdit} />
        </PageWrapper>
        <StickyBottomBar>
          <div css={stickyBottomBarBodyStyle}>
            <ButtonAction
              disabled={isLoading}
              type="button"
              onClick={handleSubmit(onSubmit)}
            >
              {isLoading ? (
                <FontAwesomeIcon icon={solid("spinner")} pulse spin />
              ) : (
                buttonText
              )}
            </ButtonAction>
          </div>
        </StickyBottomBar>
      </StyledForm>
    </FormProvider>
  );
};
